import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "../styles.module.scss";
import { fetchVehicleTypeahead, TYPEAHEAD_MIN_QUERY_LENGTH, TypeaheadVehicle } from "../../services/VehicleTypeaheadService";

const DEBOUNCE_MS = 300;

export default function VehicleTypeahead(props: {
    id?: string;
    onSelect: (vehicle: TypeaheadVehicle) => void;
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<TypeaheadVehicle[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    // Viewport-relative coords for the portaled results list — computed from
    // the input's own rect since the list renders into document.body, not
    // this component's DOM position (see the stacking-context note below).
    const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);
    // Guards against an older, slower request overwriting a newer response.
    const requestIdRef = useRef(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const q = query.trim();
        if (q.length < TYPEAHEAD_MIN_QUERY_LENGTH) {
            setResults([]);
            setIsOpen(false);
            setIsLoading(false);
            return;
        }

        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        const timer = setTimeout(async () => {
            try {
                const vehicles = await fetchVehicleTypeahead(q);
                if (requestId !== requestIdRef.current) return;
                setResults(vehicles);
                setIsOpen(true);
                setActiveIndex(-1);
                setIsLoading(false);
            } catch (e) {
                if (requestId !== requestIdRef.current) return;
                console.error("vehicle typeahead error", e);
                setResults([]);
                setIsOpen(true);
                setIsLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [query]);

    // Step 1's section can end up as (or contain) an opacity/transform-based
    // stacking context (e.g. the "locked" step-2 section right below it), which
    // can make an absolutely-positioned dropdown paint *behind* that sibling
    // regardless of z-index — z-index only orders elements within the same
    // stacking context. Rendering the list into a portal on document.body sidesteps
    // that entirely: its position is computed from the input's own rect instead.
    useLayoutEffect(() => {
        if (!isOpen) return;

        const updateCoords = () => {
            const rect = inputRef.current?.getBoundingClientRect();
            if (!rect) return;
            setCoords({ top: rect.bottom + 6, left: rect.left, width: rect.width });
        };

        updateCoords();
        window.addEventListener("scroll", updateCoords, true);
        window.addEventListener("resize", updateCoords);
        return () => {
            window.removeEventListener("scroll", updateCoords, true);
            window.removeEventListener("resize", updateCoords);
        };
    }, [isOpen]);

    const selectVehicle = (vehicle: TypeaheadVehicle) => {
        setQuery(vehicle.full_name);
        setIsOpen(false);
        setResults([]);
        props.onSelect(vehicle);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || results.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % results.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
        } else if (e.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < results.length) {
                e.preventDefault();
                selectVehicle(results[activeIndex]);
            }
        } else if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    return (
        <div className={styles.vehicle_typeahead}>
            <span className={styles.vehicle_typeahead_icon} aria-hidden="true">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M11 11L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </span>
            <input
                ref={inputRef}
                id={`${props.id}-vehicle-typeahead`}
                type="text"
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={`${props.id}-vehicle-typeahead-results`}
                aria-autocomplete="list"
                autoComplete="off"
                placeholder="Search year, make, model, trim... e.g. 2022 Ford F-150 XLT"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => results.length > 0 && setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 150)}
            />
            {isLoading ? <span className={styles.vehicle_typeahead_spinner} aria-hidden="true" /> : null}
            {isOpen && coords
                ? createPortal(
                    <ul
                        ref={listRef}
                        id={`${props.id}-vehicle-typeahead-results`}
                        role="listbox"
                        className={styles.vehicle_typeahead_results}
                        style={{ top: coords.top, left: coords.left, width: coords.width }}
                    >
                        {results.length > 0 ? (
                            results.map((vehicle, i) => (
                                <li
                                    key={vehicle.id}
                                    role="option"
                                    aria-selected={i === activeIndex}
                                    className={`${styles.vehicle_typeahead_result} ${i === activeIndex ? styles.vehicle_typeahead_result_active : ""}`}
                                    // onMouseDown (not onClick) fires before the input's onBlur closes the list.
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        selectVehicle(vehicle);
                                    }}
                                >
                                    {vehicle.full_name}
                                </li>
                            ))
                        ) : (
                            <li className={styles.vehicle_typeahead_empty}>No vehicles found</li>
                        )}
                    </ul>,
                    document.body,
                )
                : null}
        </div>
    );
}
